
import { Link } from 'react-router-dom';

export default function ArticlePartThree() {
  return (
    <div className="prose prose-lg max-w-none">
      <h2 id="property-tax-prorations-and-hoa-transfer-fees" className="text-4xl font-bold text-black mt-16 mb-6">
        Property Tax Prorations and HOA Transfer Fees
      </h2>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Property tax proration is one of the most significant—and most confusing—closing costs for El Paso sellers. In Texas,
        property taxes are paid in arrears, meaning the tax bill for the current year is not due until January 31 of the
        following year. When you sell your home mid-year, you are responsible for the taxes from January 1 through the
        closing date. This amount is calculated at closing and deducted from your proceeds.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Here is how the math works. If your annual property tax bill is $6,878 (the approximate amount on a $250,000 home in
        El Paso) and you close on July 1, you owe taxes for 181 days—roughly half the year. Your prorated tax amount would be
        approximately <strong>$3,408</strong>. If you close on October 1, you owe for 273 days, bringing your proration to
        approximately <strong>$5,140</strong>. The later in the year you close, the larger your tax proration.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        This is where El Paso's{' '}
        <Link
          to="/get-cash/el-paso-property-tax-rates"
          className="text-[#D4AF37] hover:underline font-semibold"
        >
          high property tax rates of 2.5 to 2.9 percent
        </Link>{' '}
        hit sellers especially hard. In states with lower property tax rates, the proration is a minor line item. In El
        Paso, it can easily exceed $3,000 to $5,000 depending on your home's value and closing date. Combined with{' '}
        <Link
          to="/get-cash/el-paso-homeowner-insurance-costs"
          className="text-[#D4AF37] hover:underline font-semibold"
        >
          rising insurance costs
        </Link>
        , these carrying costs add up quickly.
      </p>

      <div className="bg-gray-50 rounded-2xl p-6 md:p-8 my-10 border border-gray-200">
        <h3 className="text-xl font-bold text-black mb-6">Property Tax Proration Examples by Closing Month</h3>
        <p className="text-sm text-gray-600 mb-4">Based on $274,200 home with ~$7,500 annual property tax bill</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="py-3 pr-4 font-bold text-black">Closing Month</th>
                <th className="py-3 pr-4 font-bold text-black text-right">Days Owed</th>
                <th className="py-3 font-bold text-black text-right">Seller Proration</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">March 1</td>
                <td className="py-3 pr-4 text-right">59</td>
                <td className="py-3 text-right">$1,212</td>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <td className="py-3 pr-4 font-medium">May 1</td>
                <td className="py-3 pr-4 text-right">120</td>
                <td className="py-3 text-right">$2,466</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">July 1</td>
                <td className="py-3 pr-4 text-right">181</td>
                <td className="py-3 text-right">$3,719</td>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <td className="py-3 pr-4 font-medium">September 1</td>
                <td className="py-3 pr-4 text-right">243</td>
                <td className="py-3 text-right">$4,993</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">November 1</td>
                <td className="py-3 pr-4 text-right">304</td>
                <td className="py-3 text-right">$6,247</td>
              </tr>
              <tr className="bg-black/5 font-bold">
                <td className="py-3 pr-4 text-black">December 31</td>
                <td className="py-3 pr-4 text-right text-black">365</td>
                <td className="py-3 text-right text-black">$7,500</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-gray-500 mt-4">
          *Amounts are approximate. Actual proration depends on the exact tax bill and closing date. If you have already paid
          taxes through escrow, you may receive a credit rather than a deduction.
        </p>
      </div>

      <h3 className="text-2xl font-bold text-black mt-10 mb-4">HOA Transfer Fees and Resale Certificates</h3>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        If your El Paso home is in a homeowners association, you will likely pay HOA-related fees at closing. The most
        common is the <strong>resale certificate fee</strong>, which covers the cost of the HOA providing the buyer with
        current information about the association's finances, rules, and any outstanding assessments on your property. In
        El Paso, resale certificate fees typically range from <strong>$150 to $400</strong> depending on the management
        company.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Some HOAs also charge a <strong>transfer fee</strong> when ownership changes hands, which can range from $100 to
        $500. Additionally, if you have any unpaid HOA dues, special assessments, or fines, those will be deducted from
        your proceeds at closing. Before listing your home, request a current account statement from your HOA to avoid
        surprises. If your HOA has been a source of frustration and added costs, it is one more reason some El Paso
        homeowners choose to{' '}
        <Link
          to="/get-cash/sell-house-fast-el-paso"
          className="text-[#D4AF37] hover:underline font-semibold"
        >
          sell their house quickly
        </Link>{' '}
        and move on.
      </p>

      <div className="rounded-2xl overflow-hidden shadow-lg my-12 h-80">
        <img
          src="https://public.readdy.ai/ai/img_res/0c883643c404898564252fd50198cc78.jpg"
          alt="El Paso HOA community neighborhood with maintained homes"
          title="HOA transfer fees and resale certificates add to El Paso seller closing costs"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <h2 id="repair-credits-concessions-and-negotiated-costs" className="text-4xl font-bold text-black mt-16 mb-6">
        Repair Credits, Concessions, and Negotiated Costs
      </h2>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        After the buyer's inspection, you will almost certainly face a request for repair credits or seller concessions.
        This is one of the most variable closing costs—it can range from zero to tens of thousands of dollars depending on
        your home's condition and the buyer's negotiating leverage. In the current El Paso market, where inventory has
        been rising and buyers have more options, repair credit requests have become more aggressive.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        <strong>Repair credits</strong> are dollar amounts the seller agrees to credit the buyer at closing to cover the
        cost of needed repairs. Instead of fixing the issues yourself before closing, you reduce the buyer's closing
        costs or sale price by the estimated repair amount. Common repair credit requests in El Paso include HVAC system
        issues (often $3,000 to $8,000), roof repairs ($2,000 to $15,000), plumbing problems ($500 to $5,000),
        foundation concerns ($2,000 to $10,000), and electrical updates ($500 to $3,000).
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        <strong>Seller concessions</strong> are broader—they can include paying a portion of the buyer's closing costs,
        providing a home warranty, or covering specific fees. In El Paso, it is common for sellers to provide a{' '}
        <strong>home warranty</strong> to the buyer, typically costing $450 to $650. This one-year warranty covers major
        systems and appliances and gives the buyer peace of mind. While not legally required, refusing to provide one can
        make your home less competitive compared to other listings.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Homes with significant condition issues face the largest repair credit requests. If your property has{' '}
        <Link
          to="/get-cash/sell-house-code-violations-el-paso"
          className="text-[#D4AF37] hover:underline font-semibold"
        >
          code violations
        </Link>{' '}
        or has{' '}
        <Link
          to="/get-cash/failed-fha-va-inspection"
          className="text-[#D4AF37] hover:underline font-semibold"
        >
          failed an FHA or VA inspection
        </Link>
        , the repair credits needed to close a traditional sale can be enormous—sometimes exceeding $20,000. In these
        situations, the total closing costs can consume most or all of your equity, making a cash sale to an as‑is buyer
        a far more practical option.
      </p>

      <div className="bg-[#FFF8E7] rounded-2xl p-6 md:p-8 my-10 border border-[#D4AF37]/30">
        <div className="flex items-start gap-4">
          <span className="w-10 h-10 flex items-center justify-center bg-[#D4AF37] rounded-full flex-shrink-0 mt-1">
            <i className="ri-lightbulb-line text-white text-lg"></i>
          </span>
          <div>
            <h4 className="text-lg font-bold text-black mb-2">
              Negotiation Strategy: Get a Pre-Listing Inspection
            </h4>
            <p className="text-base text-gray-700 leading-relaxed">
              One of the smartest moves an El Paso seller can make is ordering a pre‑listing home inspection before
              putting the house on the market. This costs $300 to $500 but gives you full knowledge of your home's condition
              before buyers discover issues during their own inspection. You can either fix critical items in advance
              (often cheaper when you choose the contractor) or price the home accordingly, reducing the chance of surprise
              repair credit requests that derail negotiations late in the process.
            </p>
          </div>
        </div>
      </div>

      <h3 className="text-2xl font-bold text-black mt-10 mb-4">Mortgage Payoff and Prepayment Penalties</h3>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        If you still owe money on your mortgage, the remaining balance will be paid off from your sale proceeds at
        closing. The title company orders a payoff statement from your lender, which includes the remaining principal
        balance, any accrued interest through the expected closing date, and any fees. Most conventional mortgages do not
        have prepayment penalties, but some older loans, adjustable‑rate mortgages, or non‑traditional loans may include
        them. A prepayment penalty can range from one to three percent of the remaining balance—a significant cost that
        you should verify with your lender before listing.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        The lender also charges a <strong>reconveyance fee</strong> (sometimes called a release fee) of $50 to $150 to
        process the payoff and release the lien from the property's title. This is a minor cost but one more line item
        on your settlement statement. If you are underwater on your mortgage—meaning you owe more than the home is
        worth—the closing cost picture becomes much more complicated. In that situation, you may need to explore options
        like a{' '}
        <Link
          to="/get-cash/deed-in-lieu-vs-cash-sale-el-paso"
          className="text-[#D4AF37] hover:underline font-semibold"
        >
          deed in lieu of foreclosure versus a cash sale
        </Link>{' '}
        or negotiate a short sale with your lender.
      </p>
    </div>
  );
}
